import {
	type SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useFormsContext } from "./context/formsContext";

import { AgGridReact } from "ag-grid-react";
import CustomLoadingOverlay from "../../components/loaders/LazyLoading/LazyLoading";
import { removeInstance } from "./repositories/FormsRepository";

import { IconButton, Paper, Snackbar, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Grid2";
import AddBoxIcon from "@mui/icons-material/AddBox";
import DownloadIcon from "@mui/icons-material/GetAppOutlined";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	ICellRendererParams,
	ITooltipParams,
	MenuItemDef,
	RowClickedEvent,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import { CreateForm } from "./components/CreateForm";
import { EditInstance } from "./components/EditInstance";
import { Filters } from "./filters/Filters";

import { TransformAssetCodeModel } from "@/utils/transforms";
import {
	AssetTreePicker,
	dxtToLocalServerTime,
	ErrorModal,
	useAssetContext,
	useToken,
	useUserContext,
} from "@dexteel/mesf-core";
import type { Asset } from "@dexteel/mesf-core/dist/models/Asset";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import Alert from "@mui/lab/Alert/Alert";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import { AgGridTooltip } from "@/components/ag-grid-tooltip";
import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetNewInstance, useGetSelectedInstance } from "./hooks";
import {
	useFormInstances,
	useRefreshFormInstances,
} from "./hooks/useFormInstances";
import { useFormsFilters } from "./hooks/useFormsFilters";
import { useGetFormAssetForFilter } from "./hooks/useGetFormAssetForFilter";
import type { InstancesList } from "./models/InstancesList";
import { FilterPaper } from "@/components/page/FilterPaper";

export const Forms = () => {
	const {
		state: { allAssets },
	} = useAssetContext();

	const {
		state: {
			selectedInstanceId,
			triggerEditIntance,
			newInstanceId,
			formsForUser,
			assetCodes,
		},
		actions: {
			setSelectedInstanceId,
			setFormInstanceModifiedRows,
			setformComment,
			setApproveInitials,
			setSubmitInitials,
			setFormInstanceStatus,
			setFormInstanceSections,
			setFormInstanceQuestions,
			setTriggerEditIntance,
			setSelectedFormAsset,
			setAssetCodes,
		},
	} = useFormsContext();

	const { hasPermission } = useToken();
	const {
		state: { defaultAreaId, defaultAreaName },
	} = useUserContext();
	const [error, setError] = useState("");
	const [canSubmit, setCanSubmit] = useState<boolean>(
		hasPermission("FORMS.SubmitFormInstance" as any),
	);

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const getSelectedInstance = useGetSelectedInstance();
	const getFormAssetForFilter = useGetFormAssetForFilter();

	const filters = useFormsFilters();
	const AssetId = filters.assetId;
	const {
		rows: rowData,
		isLoading: allRowsLoading,
		error: formInstancesError,
	} = useFormInstances(filters);
	const refreshFormInstances = useRefreshFormInstances();
	const [dismissedListError, setDismissedListError] = useState<Error | null>(
		null,
	);

	// ROUTES
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const gridStyle = useMemo(
		() => ({ height: "100%", width: "100%", position: "relative" as const }),
		[],
	);

	// Modals
	const [modalCreateActive, setModalCreateActive] = useState(false);
	const [modalEditActive, setModalEditActive] = useState(false);
	const [openModalDelete, setopenModalDelete] = useState<boolean>(false);
	const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);

	// Grid Columns
	const renderStatusCell = (props: ICellRendererParams) => {
		const status = props.data.FormInstanceStatusCode;
		let color = "#ccc";
		if (status === "S") color = "#5164ff";
		if (status === "A") color = "#4ebc4e";
		return (
			<div
				style={{
					width: "16px",
					height: "16px",
					borderRadius: "50%",
					backgroundColor: color,
				}}
			/>
		);
	};

	const renderAnsweredCell = (props: ICellRendererParams) => {
		const { Answered, Total } = props.data;
		if (Answered < Total) {
			props.eGridCell.style.backgroundColor = "#FF9728";
			props.eGridCell.style.fontWeight = "bold";
		}
		return <span>{Answered === null ? 0 : Answered}</span>;
	};
	const renderWarningCell = (props: ICellRendererParams) => {
		const { Warnings } = props.data;
		if (Warnings > 0) {
			props.eGridCell.style.backgroundColor = "#FF9728";
			props.eGridCell.style.fontWeight = "bold";
		}
		return <span>{Warnings === null ? 0 : Warnings}</span>;
	};
	const renderAlertCell = (props: ICellRendererParams) => {
		const { Alerts } = props.data;
		if (Alerts > 0) {
			props.eGridCell.style.backgroundColor = "#ED5656";
			props.eGridCell.style.color = "White";
			props.eGridCell.style.fontWeight = "bold";
		}
		return <span>{Alerts === null ? 0 : Alerts}</span>;
	};
	const renderAssetNameCell = (props: ICellRendererParams) => {
		const { AssetName, AssetPath } = props.data;
		// Use AssetPath if available, otherwise just show AssetName without tooltip
		return <div title={AssetPath || AssetName}>{AssetName}</div>;
	};

	const renderDownloadButton = (props: ICellRendererParams) => {
		const { FormInstanceId, FormName, AssetName, ProductionDate, Shift, Crew } =
			props.data;
		let form = FormName.replace(/\s+/g, "").replace(/\d+/g, "").toLowerCase();
		const baseURL = `${window.location.protocol}//${window.location.hostname}${
			window.location.port ? ":" + window.location.port : ""
		}`;
		form = "pdf";
		const downloadLink = `${baseURL}/${form}/${FormInstanceId}`;
		const downloadName = `${AssetName}_${ProductionDate}_${Shift}_${Crew}.pdf`;

		return (
			<a
				href={downloadLink}
				target="_blank"
				rel="noopener noreferrer"
				download={downloadName}
			>
				<DownloadIcon />
			</a>
		);
	};

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			undefined,
			mouseEvent: e.nativeEvent,
		});
	};

	const renderExpiredCell = (params: any) => {
		if (!params.data?.IsExpired) return null;
		return (
			<div
				style={{
					height: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<div
					style={{
						height: 24,
						width: 24,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: "#800020",
					}}
				>
					<div style={{ color: "#FFF", fontWeight: 600 }}>E</div>
				</div>
			</div>
		);
	};

	const [columnDefs, setColumnDefs] = useState<ColDef[]>([
		{
			field: "",
			flex: 1,
			minWidth: 30,
			maxWidth: 30,
			filter: false,
			checkboxSelection: true,
		},
		{
			field: "ProductionDate",
			headerName: "Date",
			minWidth: 150,
			maxWidth: 120,
			flex: 1,
			valueGetter: (params) => {
				return moment(params?.data?.ProductionDate).format("MM/DD/YYYY");
			},
		},
		{
			field: "Shift",
			headerName: "Shift",
			minWidth: 40,
			maxWidth: 40,
			flex: 1,
		},
		{ field: "Crew", headerName: "Crew", minWidth: 40, maxWidth: 40, flex: 1 },
		{
			field: "FormName",
			headerName: "Form Name",
			minWidth: 100,
			maxWidth: 150,
			flex: 2,
		},
		{
			field: "AssetName",
			headerName: "Asset Name",
			minWidth: 155,
			flex: 5,
			cellRenderer: renderAssetNameCell,
		},
		{
			field: "Status",
			headerName: "Answers Status",
			minWidth: 100,
			maxWidth: 100,
			flex: 2,
		},
		{
			field: "FormInstanceStatusCode",
			headerName: "",
			minWidth: 20,
			maxWidth: 20,
			cellRenderer: renderStatusCell,
		},
		{
			field: "IsExpired",
			headerName: "Expired",
			minWidth: 90,
			maxWidth: 90,
			flex: 1,
			cellRenderer: renderExpiredCell,
			tooltipComponent: "agGridTooltip",
			tooltipValueGetter: (params: ITooltipParams) =>
				params.data?.IsExpired
					? `Form instance open for ${params.data.DaysOpen} days`
					: null,
		},
		{
			field: "SubmitUser",
			headerName: "Submit User",
			minWidth: 120,
			flex: 1,
		},
		{
			field: "SubmitTimestamp",
			headerName: "Submit Time",
			minWidth: 150,
			maxWidth: 120,
			flex: 1,
			valueFormatter: (params) =>
				dxtToLocalServerTime(params.value, "MM/dd/yy HH:mm"),
		},
		{
			field: "ApprovalUser",
			headerName: "Approval User",
			minWidth: 155,
			flex: 1,
		},
		{
			field: "ApprovalTimestamp",
			headerName: "Approval Time",
			minWidth: 150,
			maxWidth: 120,
			flex: 1,
			valueFormatter: (params) =>
				dxtToLocalServerTime(params.value, "MM/dd/yy HH:mm"),
		},
		{
			field: "Questions",
			headerName: "Total",
			minWidth: 50,
			maxWidth: 50,
			flex: 1,
			valueGetter: (params) => {
				return params?.data?.Questions == null ? 0 : params?.data?.Questions;
			},
		},
		{
			field: "Answered",
			headerName: "Answered",
			minWidth: 80,
			maxWidth: 80,
			flex: 1,
			cellRenderer: renderAnsweredCell,
		},
		{
			field: "Warnings",
			headerName: "Warnings",
			minWidth: 60,
			maxWidth: 60,
			flex: 1,
			cellRenderer: renderWarningCell,
		},
		{
			field: "Alerts",
			headerName: "Alerts",
			minWidth: 50,
			maxWidth: 50,

			flex: 1,
			cellRenderer: renderAlertCell,
		},
		{
			field: "",
			headerName: "PDF",
			minWidth: 50,
			maxWidth: 50,
			flex: 1,
			cellRenderer: renderDownloadButton,
		},
		{
			field: "",
			headerName: "",
			minWidth: 70,
			maxWidth: 70,
			flex: 1,
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid2
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid2
							container
							spacing={1}
							style={{ justifyContent: "flex-end", minWidth: 30 }}
						>
							<Grid2 size={{ xs: 12, md: 12 }}>
								<Button
									fullWidth
									style={{}}
									onClick={(e) => onActionsClick(e, params)}
								>
									<FormatListBulletedSharpIcon
										style={{ height: "auto" }}
										color="action"
									/>
								</Button>
							</Grid2>
						</Grid2>
					</Grid2>
				);
			},
		},
	]);

	const changeSearchAsset = (value: number | null) => {
		navigate(
			{ pathname: `/forms/${value}`, search: searchParams.toString() },
			{ replace: true },
		);
	};

	const downloadPDFs = async () => {
		const baseURL = `${window.location.protocol}//${window.location.hostname}${
			window.location.port ? ":" + window.location.port : ""
		}`;
		const selectedNodes = gridApi?.getSelectedNodes()
			? gridApi?.getSelectedNodes()
			: [];
		const selectedData = selectedNodes?.map((node) => node.data);
		const exportInfo: InstancesList[] = [];

		selectedData.forEach((element: any) => {
			exportInfo.push({
				InstanceId: element.FormInstanceId,
				FormName: element.FormName.replace(/\s+/g, "")
					.replace(/\d+/g, "")
					.toLowerCase(),
				ProductionDate: element.ProductionDate,
				Crew: element.Crew,
				Shift: element.Shift,
				AssetName: element.AssetName,
			});
		});
		for (const instance of exportInfo) {
			let form = instance.FormName;
			form = "pdf";
			const downloadLink = document.createElement("a");
			downloadLink.href = `${baseURL}/${form}/${instance.InstanceId}`;
			downloadLink.download = `${instance.AssetName}_${instance.ProductionDate}_${instance.Shift}_${instance.Crew}.pdf`;
			document.body.appendChild(downloadLink);
			downloadLink.click();
			document.body.removeChild(downloadLink);
			await new Promise((resolve) => setTimeout(resolve, 1000));
		}
	};

	const deleteForms = async () => {
		const selectedNodes = gridApi?.getSelectedNodes()
			? gridApi?.getSelectedNodes()
			: [];
		const selectedData = selectedNodes?.map((node) => node.data);
		const deleteInfo: InstancesList[] = [];

		selectedData.forEach((element: any) => {
			deleteInfo.push({
				InstanceId: element.FormInstanceId,
				FormName: element.FormName.replace(/\s+/g, "").toLowerCase(),
				ProductionDate: element.ProductionDate,
				Crew: element.Crew,
				Shift: element.Shift,
				AssetName: element.AssetName,
			});
		});

		deleteInfo.forEach(async (instance: any, index) => {
			removeInstance(instance.InstanceId).then((res: any) => {
				if (!res.ok) {
					setopenModalDelete(false);
					setDeleteSuccess(false);
					refreshFormInstances();

					setError((prev) => {
						if (prev) {
							return `${prev}. ${res.message}`;
						} else {
							return res.message;
						}
					});
				} else if (index === deleteInfo.length - 1) {
					setopenModalDelete(false);
					setDeleteSuccess(true);
					refreshFormInstances();
				}
			});
		});
	};

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setDeleteSuccess(false);
	};

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const result: (DefaultMenuItem | MenuItemDef)[] = [];
			const rowSelectedRightClick = params.node?.data;
			const selectedNode = gridApi?.getSelectedNodes()[0];
			const selectedData = selectedNode?.data;

			if (params.node && !params.node.isSelected()) {
				gridApi?.deselectAll();
				params.node.setSelected(true);
			}

			const selectedNodes = gridApi?.getSelectedNodes()
				? gridApi?.getSelectedNodes()
				: [];

			result.push({
				name: "Create Form Instance",
				action: () => {
					if (canSubmit) setModalCreateActive(true);
				},
				icon: '<i class="fas fa-plus"></i>',
				disabled: !canSubmit,
			});

			if (selectedNodes?.length > 1) {
				result.push(
					"separator",
					{
						name: `Download (${selectedNodes.length}) PDFs`,
						action: () => {
							downloadPDFs();
						},
						icon: '<i class="fas fa-file-pdf"></i>',
					},
					{
						name: `Delete (${selectedNodes.length}) Forms Instance`,
						action: () => {
							if (canSubmit) setopenModalDelete(true);
						},
						icon: '<i class="fas fa-trash"></i>',
						disabled: !canSubmit,
					},
				);

				return result;
			}

			if (selectedNodes?.length === 1 || rowSelectedRightClick !== undefined) {
				result.push(
					"separator",
					{
						name: "Edit Form Instance",
						action: () => {
							if (canSubmit)
								editFormInstance(
									rowSelectedRightClick
										? rowSelectedRightClick
										: selectedData[0],
								);
						},
						icon: '<i class="fas fa-edit"></i>',
						disabled: !canSubmit,
					},
					{
						name: "Delete Form Instance",
						action: () => {
							if (canSubmit) setopenModalDelete(true);
						},
						icon: '<i class="fas fa-trash"></i>',
						disabled: !canSubmit,
					},
					{
						name: "Download PDF",
						action: () => {
							downloadPDFs();
						},
						icon: '<i class="fas fa-file-pdf"></i>',
					},
				);

				return result;
			}

			return result;
		},
		[gridApi],
	);
	const onRowClicked = (params: RowClickedEvent) => {
		const {
			FormInstanceId,
			SubmitSignature,
			ApprovalSignature,
			FormInstanceComments,
			FormInstanceStatusCode,
		} = params.data;

		setSelectedInstanceId(FormInstanceId);
		setSubmitInitials(SubmitSignature || "");
		setApproveInitials(ApprovalSignature || "");
		setformComment(FormInstanceComments || "");
		setFormInstanceStatus(FormInstanceStatusCode);

		gridApi?.deselectAll();
		params.node.setSelected(true);
	};

	const editFormInstance = (rowSelected: any) => {
		const {
			FormInstanceId,
			SubmitSignature,
			ApprovalSignature,
			FormInstanceComments,
			FormInstanceStatusCode,
		} = rowSelected;
		setSelectedInstanceId(FormInstanceId);
		setSubmitInitials(SubmitSignature || "");
		setApproveInitials(ApprovalSignature || "");
		setformComment(FormInstanceComments || "");
		setFormInstanceStatus(FormInstanceStatusCode);
		if (canSubmit) setModalEditActive(true);
	};
	const onDoubleClicked = (params: RowDoubleClickedEvent) => {
		const selectedRow = params.api.getSelectedNodes();
		const {
			FormInstanceId,
			SubmitSignature,
			ApprovalSignature,
			FormInstanceComments,
			FormInstanceStatusCode,
		} = selectedRow[0].data;

		setSelectedInstanceId(FormInstanceId);
		setSubmitInitials(SubmitSignature || "");
		setApproveInitials(ApprovalSignature || "");
		setformComment(FormInstanceComments || "");
		setFormInstanceStatus(FormInstanceStatusCode);

		if (canSubmit) setModalEditActive(true);
	};
	const resetFilter = () => {
		setSelectedFormAsset(null);
		setSearchParams(new URLSearchParams(), { replace: true });
		setTimeout(() => {
			navigate(`/forms/`, { replace: true });
		}, 500);
	};

	useEffect(() => {
		if (!modalCreateActive || !modalEditActive) {
			setFormInstanceModifiedRows([]);
			setFormInstanceSections([]);
			setFormInstanceQuestions([]);
		}
		if (modalEditActive) {
			getSelectedInstance();
		}
	}, [modalCreateActive, modalEditActive]);

	useEffect(() => {
		gridApi?.forEachNode((node) => {
			if (node.data.FormInstanceId === selectedInstanceId)
				node.setSelected(true);
		});
		gridApi?.hideOverlay();
	}, [allRowsLoading]);

	useEffect(() => {
		if (triggerEditIntance) {
			setFormInstanceStatus("O");
			setformComment("");
			setApproveInitials("");
			setSubmitInitials("");
			setTriggerEditIntance(false);
			setModalEditActive(true);
			setModalCreateActive(false);
			setFormInstanceModifiedRows([]);
		}
	}, [triggerEditIntance]);

	const getNewInstance = useGetNewInstance();
	useEffect(() => {
		if (newInstanceId !== null) {
			getNewInstance().then((res) => {
				if (!res.ok) {
					setError((prev) => {
						if (prev) {
							return `${prev}. ${res.message}`;
						} else {
							return res.message;
						}
					});
				}
			});
		}
	}, [newInstanceId]);

	useEffect(() => {
		getFormAssetForFilter().then((res) => {
			if (!res.ok) {
				setError((prev) => {
					if (prev) {
						return `${prev}. ${res.message}`;
					} else {
						return res.message;
					}
				});
			}
		});
	}, []);

	return (
		<>
			<Grid2
				container
				sx={{
					padding: "10px 20px",
					height: "calc(100vh - 64px)",
					boxSizing: "border-box",
					flexDirection: "column",
					flexWrap: "nowrap",
				}}
			>
				<Grid2
					container
					size={{ xs: 12 }}
					alignItems="center"
					justifyContent="center"
					spacing={1}
					sx={{ flexShrink: 0 }}
				>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Typography
							variant="h5"
							style={{
								margin: "0",
								fontWeight: 600,
								userSelect: "none",
								marginLeft: 5,
							}}
						>
							FORMS
						</Typography>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<FilterPaper sx={{ mt: 1, mb: 2 }}>
							<Grid2 container spacing={2} alignItems="center">
								<Grid2 size={{ lg: 2, md: 12, xs: 12 }}>
									<AssetTreePicker
										label="Asset"
										value={AssetId ?? 0}
										onChange={changeSearchAsset}
									/>
								</Grid2>

								<Grid2 size={{ lg: 10, md: 12, xs: 12 }}>
									<Filters />
								</Grid2>
							</Grid2>
						</FilterPaper>
					</Grid2>
				</Grid2>

				<Grid2
					size={{ xs: 12 }}
					sx={{
						flex: 1,
						minHeight: 0,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Paper
						elevation={1}
						sx={{
							width: "100%",
							padding: "10px",
							borderRadius: "0.75rem",
							flex: 1,
							minHeight: 0,
							display: "flex",
							flexDirection: "column",
							boxSizing: "border-box",
						}}
					>
						<Grid2
							sx={{
								flex: 1,
								minHeight: 0,
								width: "100%",
								paddingBottom: "60px",
							}}
							size={{ xs: 12 }}
						>
							<div style={gridStyle}>
								<AgGridReact
									onGridReady={(params) => setGridApi(params.api)}
									rowData={rowData}
									columnDefs={columnDefs}
									defaultColDef={globalDefaultColDef}
									components={{ agGridTooltip: AgGridTooltip }}
									rowHeight={30}
									headerHeight={32}
									animateRows={true}
									loadingOverlayComponent={loadingOverlayComponent}
									getContextMenuItems={getContextMenuItems}
									rowSelection="multiple"
									onRowDoubleClicked={onDoubleClicked}
									onRowClicked={onRowClicked}
									loading={allRowsLoading}
									onCellClicked={(params) => {
										if (params.column.getColId() !== "") {
											params.node.setSelected(!params.node.isSelected());
										}
									}}
								/>
								<IconButton
									onClick={() =>
										canSubmit && setModalCreateActive(!modalCreateActive)
									}
									aria-label="add"
									color="primary"
									style={{
										padding: 0,
										position: "absolute",
										left: 0,
										bottom: -55,
									}}
								>
									<AddBoxIcon style={{ width: 35, height: 35 }} />
								</IconButton>
							</div>
						</Grid2>
					</Paper>
				</Grid2>
			</Grid2>

			<CreateForm
				show={modalCreateActive}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) refreshFormInstances();
					setModalCreateActive(false);
				}}
				suffixTitle="Forms"
			/>

			<EditInstance
				show={modalEditActive}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) refreshFormInstances();
					setModalEditActive(false);
				}}
				suffixTitle="Forms"
			/>

			<Dialog open={openModalDelete} aria-labelledby="responsive-dialog-title">
				<DialogTitle id="responsive-dialog-title">WARNING</DialogTitle>
				<DialogContent>
					<DialogContentText style={{ padding: 0, margin: 0 }}>
						Be careful, once deleted you will not be able to recover this form
						instance, are you sure you want to delete it?
					</DialogContentText>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px" }}>
					<Button
						onClick={() => setopenModalDelete(false)}
						autoFocus
						color="inherit"
					>
						CANCEL
					</Button>
					<Button autoFocus color="secondary" onClick={deleteForms}>
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={deleteSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={handleClose}>
					Form Deleted Successfully
				</Alert>
			</Snackbar>

			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={
					formInstancesError && formInstancesError !== dismissedListError
						? formInstancesError.message
						: ""
				}
				onHide={() => setDismissedListError(formInstancesError)}
			/>
		</>
	);
};
